import { useContext, useId, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from "react";

import {
  correspondenceFileUrl,
  type AttachmentOut,
  type CorrespondenceKind,
  type CorrespondenceOut,
  type TimelineEventOut,
  type TimelineItemOut,
} from "@/api";
import {
  DownloadIcon,
  FileIcon,
  MailInIcon,
  MailOutIcon,
  NoteIcon,
  PaperclipIcon,
  SystemIcon,
} from "@/components/icons";
import { formatBytes, formatDateTime, formatDay } from "@/lib/format";

import {
  ADOPT_ITEMS_MAX,
  adoptCandidates,
  beiwerkHideLabel,
  beiwerkLabel,
  beiwerkName,
  chooserLabel,
  splitAttachments,
} from "./attachments";
import { EntryTime, MeasureText, NBSP } from "./EntryParts";
import { Fold } from "./Fold";
import { EntryMarksContext } from "./entryMarks";
import { EntryMenu, type MenuItem } from "./EntryMenu";
import styles from "./Record.module.css";

interface Kind {
  Icon: ComponentType<{ size?: number; className?: string }>;
  /** Read out before the heading. */
  word: string;
  /** The word the meta line begins with — the direction without colour or arrow (CI §7.4). */
  direction: string | null;
}

/** Symbol, spoken word and direction per kind (UX ruling 6: SVG, no status colour). */
const KIND: Record<CorrespondenceKind, Kind> = {
  email_in: { Icon: MailInIcon, word: "Eingehende Mail", direction: "Eingang" },
  email_out: { Icon: MailOutIcon, word: "Ausgehende Mail", direction: "Ausgang" },
  note: { Icon: NoteIcon, word: "Notiz", direction: null },
  file: { Icon: FileIcon, word: "Datei", direction: null },
};

/**
 * The folded preview (record fix wave I5): runs of blank lines taken out, so
 * a greeting's empty line does not take one of the two lines a person sees —
 * mails showed „Guten Tag …," and „…" and nothing of what they said.
 */
export function foldBlankLines(text: string): string {
  return text.replace(/\n(?:[ \t]*\n)+/g, "\n");
}

/**
 * Whether the folded text is cut **at the width it has now** (record fix
 * wave I5) — measured, not counted: 180 characters is two lines at 1440 px
 * and five at 390 px, and a short note in a narrow column was cut with no
 * way to read the rest. Measured again whenever the text box changes size;
 * not measured while unfolded, where nothing is cut.
 */
function useCut(folded: boolean, text: string) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || !folded) return undefined;
    const measure = () => setCut(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [folded, text]);
  return [ref, cut] as const;
}

/** „a → b · Cc c" — one line, the whole of it in `title`. `null` for a note. */
function addressLine(entry: CorrespondenceOut): string | null {
  if (entry.fromAddr === null && entry.toAddrs.length === 0) return null;
  const to = entry.toAddrs.length > 0 ? entry.toAddrs.join(", ") : "—";
  const cc = entry.ccAddrs.length > 0 ? ` · Cc ${entry.ccAddrs.join(", ")}` : "";
  return `${entry.fromAddr ?? "—"} → ${to}${cc}`;
}

/**
 * The one way to save a file in the record (UX gate round 4, #3): the same
 * ghost button „↓ Herunterladen" at a filed file, at every file of a mail
 * and in „Zeichnungen" — it was a framed button, a paperclip chip, a text
 * link and, on a phone, an arrow alone. The word stays visible at every
 * width; the file's name is in its accessible name and its `title`, so a
 * list of them read on its own still says which is which. Never previewed,
 * never rendered (design §6.4).
 */
export function DownloadLink({
  href,
  name,
  filename,
  className,
}: {
  href: string;
  /** The file's name, for the accessible name and the tooltip. */
  name: string;
  /** The name to save under, where the route does not send one (the page route). */
  filename?: string;
  className?: string;
}) {
  return (
    <a
      className={`${styles.download} ${className ?? ""}`}
      href={href}
      download={filename ?? true}
      title={name}
      aria-label={`${name} herunterladen`}
    >
      <DownloadIcon size={12} />
      <span>Herunterladen</span>
    </a>
  );
}

/**
 * A file of a mail, in one row (UX gate round 4, #3): what it is — the
 * paperclip, its name, its size — and the one „Herunterladen". Replaces the
 * chip, which was a download of its own kind.
 */
function FileLine({ href, name, size, children }: { href: string; name: string; size?: number; children?: ReactNode }) {
  return (
    <li className={styles.fileLine}>
      <PaperclipIcon size={12} className={styles.fileClip} />
      <span className={styles.fileLineName} title={name}>
        {name}
      </span>
      {size !== undefined ? <span className={`tabular ${styles.fileLineSize}`}>{formatBytes(size)}</span> : null}
      <DownloadLink href={href} name={name} />
      {children}
    </li>
  );
}

/**
 * A mail's small pictures behind one text button (design 2026-10-24 §6.2,
 * §8.5): „+ 16 Signaturbilder" — 2026-0014's mail listed every one of them,
 * and the page ran on „endlessly". Open, they wrap as chips (UX-Gutachten 1,
 * B-18): as file lines the sixteen were a list of sixteen rows again,
 * ~450 px. What a chip is, is the caller's (`chip`): on the entry each is
 * that file's own download, named as every download in the record is
 * („… herunterladen"); in the chooser each adopts its file (`AdoptDialog`,
 * UX-Gutachten 2, B-1) — folded there the same way, so nothing is lost.
 */
export function BeiwerkFold({
  beiwerk,
  chip,
  defaultOpen = false,
}: {
  beiwerk: readonly AttachmentOut[];
  /** One file's chip — the element inside its list item. */
  chip: (attachment: AttachmentOut) => ReactNode;
  /** Open from the start — where there is nothing else to choose from. */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className={styles.beiwerk}>
      <button
        type="button"
        className={styles.more}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((shown) => !shown)}
      >
        {open ? beiwerkHideLabel(beiwerk) : beiwerkLabel(beiwerk)}
      </button>
      <Fold id={id} open={open}>
        <ul className={styles.chips} aria-label={beiwerkName(beiwerk)}>
          {beiwerk.map((attachment) => (
            <li key={attachment.id}>{chip(attachment)}</li>
          ))}
        </ul>
      </Fold>
    </div>
  );
}

/** A small picture's chip on the entry: its own download — name · size · the arrow. */
function DownloadChip({ href, attachment }: { href: string; attachment: AttachmentOut }) {
  return (
    <a
      className={styles.chip}
      href={href}
      download
      title={`${attachment.filename} · ${formatBytes(attachment.byteSize)}`}
      aria-label={`${attachment.filename} herunterladen`}
    >
      <span className={styles.chipName}>{attachment.filename}</span>
      <span className={`tabular ${styles.chipSize}`}>{formatBytes(attachment.byteSize)}</span>
      <DownloadIcon size={12} className={styles.chipIcon} />
    </a>
  );
}

/** Words a German note abbreviates with a full stop — no sentence ends there. */
const ABBREVIATIONS = new Set([
  "ca", "bzw", "ggf", "usw", "evtl", "inkl", "zzgl", "exkl", "vgl", "etc", "nr", "str", "tel", "hr", "fr",
  "dr", "abs", "bspw", "max", "min", "mind", "gem", "lt", "sog", "incl", "zb",
]);

/** Longest title a note's first sentence may take before it is cut (UX gate round 4, #3). */
const TITLE_MAX = 120;

/**
 * A note's title line and what follows it (UX gate round 4, #3): its first
 * sentence — a heading „Notiz" said nothing the pencil did not say already.
 * A sentence ends at „." „!" „?" before a capital, a quote or the end, not
 * after an abbreviation („ca. 4,20", „z. B."); a line break ends it too. A
 * first sentence longer than the line can hold is cut at a word and goes on
 * in the text under it — nothing is said twice, nothing is lost.
 */
export function splitNote(text: string): { title: string; rest: string } {
  const body = text.trim();
  let end = body.length;
  const lineBreak = body.indexOf("\n");
  if (lineBreak >= 0) end = lineBreak;
  const stop = /[.!?…](?=\s+["„“‚A-ZÄÖÜ0-9]|\s*$)/g;
  for (let match = stop.exec(body); match !== null && match.index < end; match = stop.exec(body)) {
    const word = /([\p{L}]+)$/u.exec(body.slice(0, match.index))?.[1] ?? "";
    const abbreviation = body[match.index] === "." && (word.length === 1 || ABBREVIATIONS.has(word.toLowerCase()));
    if (!abbreviation) {
      end = match.index + 1;
      break;
    }
  }
  let title = body.slice(0, end).trim();
  let rest = body.slice(end).replace(/^\s+/, "");
  if (title.length > TITLE_MAX) {
    const cut = title.lastIndexOf(" ", TITLE_MAX);
    const at = cut > TITLE_MAX / 2 ? cut : TITLE_MAX;
    rest = `…${title.slice(at).trimStart()}${rest !== "" ? ` ${rest}` : ""}`;
    title = `${title.slice(0, at).trimEnd()} …`;
  }
  return { title, rest };
}

/**
 * A system event in one line (UX gate round 1, #9): what happened, who did
 * it, when — symbol without its box, the text a step quieter. Fifteen of
 * seventeen entries were system events, each as tall as a mail; the record
 * read like a log with some correspondence in it.
 *
 * On a phone (≤ 600 px) the actor takes a meta line of its own (round-3
 * screen check, R3-7): glued to the last word, the name pulled that word
 * onto its line and left „Zeichnung" or „da." standing alone.
 */
function EventEntry({ event }: { event: TimelineEventOut }) {
  return (
    <article className={`${styles.entry} ${styles.system}`} aria-label={event.summary}>
      <span className={styles.icon} aria-hidden="true">
        <SystemIcon size={14} />
      </span>
      <p className={styles.systemLine}>
        <span className="visually-hidden">Systemereignis: </span>
        <MeasureText text={event.summary} />
        {/* Wide: glued to the sentence's last word and never broken itself
            (UX gate round 2, #6) — a line never begins with „· Denis
            Muster". Narrow: a line of its own, without the dot (R3-7). */}
        <span className={styles.systemActor}>
          <span className={styles.systemDot}>{NBSP}· </span>
          {event.actorDisplayName ?? "System"}
        </span>
      </p>
      {/* The time in the column every entry's time stands in (UX gate
          round 4, #3): an empty place where the others have their ⋮. */}
      <div className={styles.entrySide}>
        <EntryTime at={event.at} title={formatDateTime(event.at)} />
        <span className={styles.menuSlot} aria-hidden="true" />
      </div>
    </article>
  );
}

/**
 * The types the reading takes — the server's `DOCUMENT_MIME`, the same three
 * `DropArea` accepts. Only for these does `adoptable: false` say something
 * about the file itself (round-2 screen check, R2-7): the server answers
 * `false` for exactly two reasons, a type the reading cannot read or a file
 * that is a document of the Vorgang already. For a readable type only the
 * second is left.
 */
const READABLE = new Set(["application/pdf", "image/jpeg", "image/png"]);

/** Already under „Zeichnungen": adopted just now, or known to be (R2-7) — after a reload too. */
function inDocuments(attachment: AttachmentOut, adoptedHere: boolean): boolean {
  return adoptedHere || (!attachment.adoptable && READABLE.has(attachment.mimeType));
}

function CorrespondenceEntry({ caseId, entry }: { caseId: string; entry: CorrespondenceOut }) {
  const [open, setOpen] = useState(false);
  const { fresh, adopted, onRetract, onAdopt, onChooseDrawing, onCustomer } = useContext(EntryMarksContext);
  const kind = KIND[entry.kind] ?? KIND.file;
  const retracted = entry.retractedAt !== null;
  const note = entry.kind === "note" ? splitNote(entry.bodyText ?? "") : null;
  // A note's title line is its first sentence; the text under it is the rest.
  const body = note !== null ? note.rest : (entry.bodyText ?? "");
  const shown = open ? body : foldBlankLines(body);
  const [textRef, cut] = useCut(!open, shown);
  const heading =
    note !== null && note.title !== ""
      ? note.title
      : (entry.subject ?? (entry.kind === "note" ? kind.word : (entry.sourceFilename ?? kind.word)));
  const addresses = addressLine(entry);
  // A filed file is its own title: the download stands beside its name.
  // A mail lists its original with its attachments, each with the same one.
  const ownFile = entry.kind === "file" && entry.hasSource;
  const sourceName = entry.sourceFilename ?? "Original";
  const listed = entry.hasSource && !ownFile;
  const inDrawings = (attachment: AttachmentOut) => inDocuments(attachment, adopted.get(attachment.id)?.done === true);
  // Small pictures that are no drawing fold behind one toggle (§6.2, §8.5);
  // „adopted" is what the „→ Zeichnungen" mark already knows.
  const { shown: inSight, beiwerk } = splitAttachments(entry.attachments, (id) =>
    entry.attachments.some((attachment) => attachment.id === id && inDrawings(attachment)),
  );
  const hasFiles = listed || inSight.length > 0;
  const notes = entry.attachments.flatMap((attachment) => {
    const mark = adopted.get(attachment.id);
    return mark !== undefined ? [mark.sentence] : [];
  });

  // The one menu (UX ruling 8): the original, the adoptions, the customer of
  // a mail, then — apart — the retraction. A retracted entry keeps only its
  // downloads.
  const menu: MenuItem[] = [];
  if (entry.hasSource) {
    menu.push({ key: "source", label: "Original herunterladen", href: correspondenceFileUrl(caseId, entry.id) });
  }
  // Up to two files, an item each; from three on, one item and the chooser
  // (UX-Gutachten 2, B-1) — sixteen signature pictures were sixteen items,
  // and the menu ran over the masthead. The Beiwerk has no item of its own.
  const candidates = adoptCandidates(entry);
  if (!retracted && onChooseDrawing !== undefined && candidates.length > ADOPT_ITEMS_MAX) {
    menu.push({ key: "adopt", label: chooserLabel(candidates), onSelect: () => onChooseDrawing(entry) });
  } else if (!retracted && onAdopt !== undefined) {
    for (const attachment of candidates) {
      menu.push({
        key: `adopt-${attachment.id}`,
        label: `„${attachment.filename}“ als Zeichnung übernehmen`,
        onSelect: () => onAdopt(entry, attachment),
      });
    }
  }
  // A mail names who wrote — any mail of the record, also one filed before
  // this existed; a note or a file has no sender to read (spec 2026-10-20 §3.3).
  if (!retracted && onCustomer !== undefined && (entry.kind === "email_in" || entry.kind === "email_out")) {
    menu.push({ key: "customer", label: "Kunde aus dieser Mail", onSelect: () => onCustomer(entry) });
  }
  if (!retracted && onRetract !== undefined) {
    menu.push({
      key: "retract",
      label: "Eintrag zurückziehen …",
      onSelect: () => onRetract(entry),
      separatorBefore: menu.length > 0,
    });
  }

  // The meta line (UX gate round 4, #3): what kind and from whom, in one
  // quiet line under the title — a mail its direction and addresses, a note
  // its author, a file who filed it (its author is not who put it here).
  // A retraction says so first.
  const who =
    entry.kind === "note" ? (
      <>
        <span className={styles.direction}>{kind.word}</span> · <span>{entry.createdByDisplayName}</span>
      </>
    ) : entry.kind === "file" ? (
      <>
        <span>abgelegt von {entry.createdByDisplayName}</span>
        {addresses !== null ? ` · ${addresses}` : null}
      </>
    ) : null;

  return (
    // `id` and `tabIndex={-1}`: a place focus can be put — „Zum Eintrag" after
    // a duplicate (Task 8), the entry itself after a retraction.
    <article
      id={`entry-${entry.id}`}
      tabIndex={-1}
      className={`${styles.entry} ${retracted ? styles.retracted : ""}`}
      aria-label={heading}
      data-new={fresh.has(entry.id) ? "" : undefined}
    >
      <span className={styles.icon} aria-hidden="true">
        <kind.Icon />
      </span>
      <div className={styles.entryBody}>
        {/* One grid for every entry (UX gate round 4, #3): symbol · title
            line · meta line · time with its ⋮ beside it, on the title's
            line. The title is the file's name, the mail's subject or the
            note's first sentence — never a bare „Notiz". */}
        <div className={styles.titleRow}>
          <p className={`${styles.subject} ${ownFile ? styles.subjectFile : ""}`} title={ownFile ? heading : undefined}>
            <span className="visually-hidden">{kind.word}: </span>
            {heading}
          </p>
          {ownFile ? (
            <DownloadLink
              className={styles.titleDownload}
              href={correspondenceFileUrl(caseId, entry.id)}
              name={sourceName}
            />
          ) : null}
        </div>
        <p
          className={`${styles.entryMeta} ${addresses !== null ? styles.oneLine : ""}`}
          title={addresses ?? undefined}
        >
          {retracted ? (
            <>
              <span className={styles.retractedMark}>Zurückgezogen</span> ·{" "}
            </>
          ) : null}
          {who ?? (
            <>
              {kind.direction !== null ? (
                <span className={styles.direction}>
                  {kind.direction}
                  {addresses !== null ? " · " : ""}
                </span>
              ) : null}
              {addresses}
            </>
          )}
        </p>
        {/* Text only, always (design §6.4): React escapes it, nothing a customer
            sent becomes markup, and no image or tracking pixel is ever fetched.
            The server caps a body at 200,000 characters („[…gekürzt]", Plan B). */}
        {body !== "" ? (
          <p ref={textRef} className={`${styles.text} ${open ? "" : styles.clamped}`}>
            {shown}
          </p>
        ) : null}
        {open || cut ? (
          <button type="button" className={styles.more} aria-expanded={open} onClick={() => setOpen((shown) => !shown)}>
            {open ? "Weniger zeigen" : "Ganzen Text zeigen"}
          </button>
        ) : null}
        {entry.parseNote !== null ? <p className={styles.parseNote}>{entry.parseNote}</p> : null}
        {hasFiles ? (
          <ul className={styles.files} aria-label="Dateien">
            {listed ? <FileLine href={correspondenceFileUrl(caseId, entry.id)} name={sourceName} /> : null}
            {inSight.map((attachment: AttachmentOut) => (
              <FileLine
                key={attachment.id}
                href={correspondenceFileUrl(caseId, entry.id, attachment.id)}
                name={attachment.filename}
                size={attachment.byteSize}
              >
                {inDrawings(attachment) ? <span className={styles.chipSuffix}>→ Zeichnungen</span> : null}
              </FileLine>
            ))}
          </ul>
        ) : null}
        {beiwerk.length > 0 ? (
          <BeiwerkFold
            beiwerk={beiwerk}
            chip={(attachment) => (
              <DownloadChip href={correspondenceFileUrl(caseId, entry.id, attachment.id)} attachment={attachment} />
            )}
          />
        ) : null}
        {notes.map((sentence) => (
          <p key={sentence} className={styles.entryNote}>
            {sentence}
          </p>
        ))}
        {retracted ? (
          <p className={styles.retractedNote}>
            Zurückgezogen am {formatDay(entry.retractedAt!)}: {entry.retractedReason}
          </p>
        ) : null}
      </div>
      {/* Time and ⋮ side by side on the title's line, for every entry
          (UX gate round 4, #3): the ⋮ under the time left a one-line entry
          30 px taller than its content. */}
      <div className={styles.entrySide}>
        <EntryTime
          at={entry.occurredAt}
          title={`${formatDateTime(entry.occurredAt)} · abgelegt von ${entry.createdByDisplayName}`}
        />
        <EntryMenu label={`Aktionen zu „${heading}“`} items={menu} />
      </div>
    </article>
  );
}

export function TimelineEntry({ caseId, item }: { caseId: string; item: TimelineItemOut }) {
  if (item.kind === "event") return <EventEntry event={item.event} />;
  return <CorrespondenceEntry caseId={caseId} entry={item.correspondence} />;
}
